import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Users, Package, Shield, UserCog, DollarSign,
  Download, Calendar, PieChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatCurrency, formatDate } from '../utils/helpers';

const reportTypes = [
  { id: 'sales', label: 'Sales Reports', icon: TrendingUp, color: 'from-[#7FA86A] to-[#96BE82]' },
  { id: 'revenue', label: 'Revenue Reports', icon: DollarSign, color: 'from-emerald-400 to-emerald-600' },
  { id: 'expense', label: 'Expense Reports', icon: BarChart3, color: 'from-red-400 to-red-600' },
  { id: 'inventory', label: 'Inventory Reports', icon: Package, color: 'from-blue-400 to-blue-600' },
  { id: 'warranty', label: 'Warranty Reports', icon: Shield, color: 'from-violet-400 to-violet-600' },
  { id: 'customer', label: 'Customer Reports', icon: Users, color: 'from-cyan-400 to-cyan-600' },
  { id: 'technician', label: 'Technician Reports', icon: UserCog, color: 'from-amber-400 to-amber-600' },
  { id: 'profit', label: 'Profit Reports', icon: PieChart, color: 'from-rose-400 to-rose-600' },
];

export default function ReportsPage() {
  const { invoices, products, customers, staff, warranties, financeRecords, projects } = useAdminStore();
  const [selectedReport, setSelectedReport] = useState('sales');
  const [dateRange, setDateRange] = useState('month');

  const data = useMemo(() => {
    const totalRevenue = financeRecords.filter(f => f.type === 'revenue').reduce((s, f) => s + f.amount, 0);
    const totalExpenses = financeRecords.filter(f => f.type === 'expense').reduce((s, f) => s + f.amount, 0);
    const totalPaid = invoices.filter(i => i.paymentStatus === 'paid').reduce((s, i) => s + i.total, 0);
    const totalPending = invoices.filter(i => i.paymentStatus === 'pending' || i.paymentStatus === 'partial').reduce((s, i) => s + i.balance, 0);
    const totalStockValue = products.reduce((s, p) => s + p.stock * (p.costPrice || 0), 0);
    const lowStock = products.filter(p => (p.stock - p.reservedStock) <= 5);
    const activeWarranties = warranties.filter(w => w.status === 'active');
    const expiredWarranties = warranties.filter(w => w.status === 'expired');
    const completedProjects = projects.filter(p => p.status === 'completed');
    const activeProjects = projects.filter(p => p.status === 'installation' || p.status === 'approved');
    const totalStaffSalary = staff.filter(s => s.salary).reduce((s, st) => s + (st.salary || 0), 0);
    return {
      totalRevenue, totalExpenses, totalPaid, totalPending, totalStockValue,
      lowStock, activeWarranties, expiredWarranties, completedProjects,
      activeProjects, totalStaffSalary, profit: totalRevenue - totalExpenses,
    };
  }, [financeRecords, invoices, products, warranties, projects, staff]);

  const renderReport = () => {
    switch (selectedReport) {
      case 'sales':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">Total Invoices</span><p className="text-[20px] font-bold text-gray-900 mt-1">{invoices.length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">Total Revenue</span><p className="text-[20px] font-bold text-gray-900 mt-1">{formatCurrency(data.totalRevenue)}</p></div>
              <div className="bg-emerald-50/80 rounded-xl p-4"><span className="text-[11px] text-emerald-500 uppercase font-semibold">Paid</span><p className="text-[20px] font-bold text-emerald-700 mt-1">{formatCurrency(data.totalPaid)}</p></div>
              <div className="bg-amber-50/80 rounded-xl p-4"><span className="text-[11px] text-amber-500 uppercase font-semibold">Outstanding</span><p className="text-[20px] font-bold text-amber-700 mt-1">{formatCurrency(data.totalPending)}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Invoice</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Customer</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Amount</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Paid</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Status</th>
                </tr></thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{inv.number}</td>
                      <td className="px-4 py-3 text-[13px] text-gray-600">{inv.customerName}</td>
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900 text-right">{formatCurrency(inv.total)}</td>
                      <td className="px-4 py-3 text-[13px] text-gray-600 text-right">{formatCurrency(inv.amountPaid)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${inv.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-600' : inv.paymentStatus === 'partial' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>{inv.paymentStatus}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'revenue':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-100"><span className="text-[11px] text-emerald-500 uppercase font-semibold">Total Revenue</span><p className="text-[24px] font-bold text-emerald-700 mt-1">{formatCurrency(data.totalRevenue)}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Finance Records</span><p className="text-[24px] font-bold text-gray-900 mt-1">{financeRecords.filter(f => f.type === 'revenue').length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Avg per Record</span><p className="text-[24px] font-bold text-gray-900 mt-1">{formatCurrency(data.totalRevenue / (financeRecords.filter(f => f.type === 'revenue').length || 1))}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Description</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Category</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Amount</th>
                </tr></thead>
                <tbody>
                  {financeRecords.filter(f => f.type === 'revenue').map(r => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-[12px] text-gray-500">{formatDate(r.date)}</td>
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{r.description}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-500">{r.category}</td>
                      <td className="px-4 py-3 text-[13px] font-semibold text-emerald-600 text-right">{formatCurrency(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'expense':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-red-50/80 rounded-xl p-5 border border-red-100"><span className="text-[11px] text-red-500 uppercase font-semibold">Total Expenses</span><p className="text-[24px] font-bold text-red-700 mt-1">{formatCurrency(data.totalExpenses)}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Staff Salary</span><p className="text-[24px] font-bold text-gray-900 mt-1">{formatCurrency(data.totalStaffSalary)}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Other Expenses</span><p className="text-[24px] font-bold text-gray-900 mt-1">{formatCurrency(data.totalExpenses - data.totalStaffSalary)}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Description</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Category</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Amount</th>
                </tr></thead>
                <tbody>
                  {financeRecords.filter(f => f.type === 'expense').map(r => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-[12px] text-gray-500">{formatDate(r.date)}</td>
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{r.description}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-500">{r.category}</td>
                      <td className="px-4 py-3 text-[13px] font-semibold text-red-500 text-right">{formatCurrency(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'inventory':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50/80 rounded-xl p-4"><span className="text-[11px] text-blue-500 uppercase font-semibold">Total Products</span><p className="text-[20px] font-bold text-gray-900 mt-1">{products.length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">Total Stock</span><p className="text-[20px] font-bold text-gray-900 mt-1">{products.reduce((s, p) => s + p.stock, 0)}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">Stock Value</span><p className="text-[20px] font-bold text-gray-900 mt-1">{formatCurrency(data.totalStockValue)}</p></div>
              <div className="bg-red-50/80 rounded-xl p-4"><span className="text-[11px] text-red-500 uppercase font-semibold">Low Stock</span><p className="text-[20px] font-bold text-red-600 mt-1">{data.lowStock.length}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Product</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">SKU</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Stock</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Available</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Value</th>
                </tr></thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{p.name}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-500">{p.sku}</td>
                      <td className="px-4 py-3 text-[13px] text-gray-700 text-right">{p.stock}</td>
                      <td className="px-4 py-3 text-right"><span className={`text-[13px] font-medium ${(p.stock - p.reservedStock) <= 5 ? 'text-red-500' : 'text-gray-700'}`}>{p.stock - p.reservedStock}</span></td>
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900 text-right">{formatCurrency(p.stock * (p.costPrice || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'warranty':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-violet-50/80 rounded-xl p-5 border border-violet-100"><span className="text-[11px] text-violet-500 uppercase font-semibold">Active Warranties</span><p className="text-[24px] font-bold text-violet-700 mt-1">{data.activeWarranties.length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Total Warranties</span><p className="text-[24px] font-bold text-gray-900 mt-1">{warranties.length}</p></div>
              <div className="bg-red-50/80 rounded-xl p-5"><span className="text-[11px] text-red-500 uppercase font-semibold">Expired</span><p className="text-[24px] font-bold text-red-600 mt-1">{data.expiredWarranties.length}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Customer</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Product</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Installed</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Expires</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Status</th>
                </tr></thead>
                <tbody>
                  {warranties.map(w => (
                    <tr key={w.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{w.customerName}</td>
                      <td className="px-4 py-3 text-[13px] text-gray-600">{w.products[0]?.productName}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-500">{formatDate(w.installationDate)}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-500">{formatDate(w.expiryDate)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${w.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>{w.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'customer':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-cyan-50/80 rounded-xl p-4"><span className="text-[11px] text-cyan-500 uppercase font-semibold">Total Customers</span><p className="text-[20px] font-bold text-gray-900 mt-1">{customers.length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">Avg Spend</span><p className="text-[20px] font-bold text-gray-900 mt-1">{formatCurrency(customers.reduce((s, c) => s + c.totalSpent, 0) / (customers.length || 1))}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">VIP Customers</span><p className="text-[20px] font-bold text-gray-900 mt-1">{customers.filter(c => c.tags?.includes('VIP')).length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-4"><span className="text-[11px] text-gray-400 uppercase font-semibold">Corporate</span><p className="text-[20px] font-bold text-gray-900 mt-1">{customers.filter(c => c.tags?.includes('Corporate')).length}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Customer</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">City</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Projects</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Invoices</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Total Spent</th>
                </tr></thead>
                <tbody>
                  {customers.sort((a, b) => b.totalSpent - a.totalSpent).map(c => (
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3"><div className="text-[13px] font-medium text-gray-900">{c.name}</div><div className="text-[11px] text-gray-400">{c.mobile}</div></td>
                      <td className="px-4 py-3 text-[13px] text-gray-600">{c.city || '—'}</td>
                      <td className="px-4 py-3 text-[13px] text-gray-700 text-right">{c.totalProjects}</td>
                      <td className="px-4 py-3 text-[13px] text-gray-700 text-right">{c.totalInvoices}</td>
                      <td className="px-4 py-3 text-[13px] font-semibold text-gray-900 text-right">{formatCurrency(c.totalSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'technician':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-100"><span className="text-[11px] text-amber-500 uppercase font-semibold">Active Technicians</span><p className="text-[24px] font-bold text-amber-700 mt-1">{staff.filter(s => s.role === 'technician' && s.isActive).length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Total Staff</span><p className="text-[24px] font-bold text-gray-900 mt-1">{staff.length}</p></div>
              <div className="bg-gray-50/80 rounded-xl p-5"><span className="text-[11px] text-gray-400 uppercase font-semibold">Monthly Payroll</span><p className="text-[24px] font-bold text-gray-900 mt-1">{formatCurrency(data.totalStaffSalary)}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Skills</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Salary</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase">Status</th>
                </tr></thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{s.name}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-500 capitalize">{s.role}</td>
                      <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{(s.skills || []).slice(0, 2).map(sk => <span key={sk} className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-600">{sk}</span>)}{(s.skills || []).length > 2 && <span className="text-[10px] text-gray-400">+{s.skills!.length - 2}</span>}</div></td>
                      <td className="px-4 py-3 text-[13px] text-gray-700 text-right">{s.salary ? formatCurrency(s.salary) : '—'}</td>
                      <td className="px-4 py-3"><span className={`w-2 h-2 rounded-full inline-block ${s.isActive ? 'bg-emerald-400' : 'bg-gray-300'}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'profit':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-100"><span className="text-[11px] text-emerald-500 uppercase font-semibold">Revenue</span><p className="text-[24px] font-bold text-emerald-700 mt-1">{formatCurrency(data.totalRevenue)}</p></div>
              <div className="bg-red-50/80 rounded-xl p-5 border border-red-100"><span className="text-[11px] text-red-500 uppercase font-semibold">Expenses</span><p className="text-[24px] font-bold text-red-700 mt-1">{formatCurrency(data.totalExpenses)}</p></div>
              <div className="bg-gradient-to-br from-[#7FA86A]/5 to-[#7FA86A]/10 rounded-xl p-5 border border-[#7FA86A]/20"><span className="text-[11px] text-[#7FA86A] uppercase font-semibold">Net Profit</span><p className="text-[24px] font-bold text-[#658C53] mt-1">{formatCurrency(data.profit)}</p></div>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl p-6">
              <h4 className="text-[14px] font-semibold text-gray-900 mb-4">Profit Margin</h4>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#7FA86A] to-[#96BE82] rounded-full transition-all" style={{ width: `${Math.min(100, (data.profit / data.totalRevenue) * 100)}%` }} />
              </div>
              <p className="text-[13px] text-gray-500 mt-2">{((data.profit / data.totalRevenue) * 100).toFixed(1)}% profit margin</p>
            </div>
            <div className="bg-white border border-gray-200/60 rounded-xl p-6">
              <h4 className="text-[14px] font-semibold text-gray-900 mb-4">Expense Breakdown</h4>
              <div className="space-y-3">
                {['Staff Salary', 'Office Rent', 'Inventory Purchase', 'Fuel & Transport', 'Other'].map(cat => {
                  const catAmount = financeRecords.filter(f => f.type === 'expense' && f.category === cat).reduce((s, f) => s + f.amount, 0);
                  const pct = data.totalExpenses > 0 ? (catAmount / data.totalExpenses) * 100 : 0;
                  return (
                    <div key={cat} className="flex items-center gap-3">
                      <span className="text-[12px] text-gray-600 w-32">{cat}</span>
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[12px] font-medium text-gray-700 w-24 text-right">{pct > 0 ? formatCurrency(catAmount) : '—'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-gray-50 rounded-2xl p-12 text-center">
            <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-[14px] text-gray-500 font-medium">Report data loading...</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Reports</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">Business analytics and insights</p>
        </div>
        <button className="h-10 px-5 rounded-xl bg-white border border-gray-200/60 text-[13px] text-gray-600 font-medium hover:bg-gray-50 flex items-center gap-2">
          <Download className="w-4 h-4" />Export PDF
        </button>
      </div>

      {/* Report Type Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          return (
            <button key={report.id} onClick={() => setSelectedReport(report.id)}
              className={`p-4 rounded-2xl border transition-all text-left ${selectedReport === report.id ? 'bg-gray-900 text-white border-gray-900 shadow-lg shadow-gray-900/10' : 'bg-white border-gray-200/60 hover:shadow-lg hover:shadow-gray-200/40'}`}>
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${report.color} flex items-center justify-center mb-3 ${selectedReport === report.id ? 'opacity-90' : ''}`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div className={`text-[13px] font-medium ${selectedReport === report.id ? 'text-white' : 'text-gray-700'}`}>{report.label}</div>
            </button>
          );
        })}
      </div>

      {/* Date Range */}
      <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden w-fit">
        {['week', 'month', 'quarter', 'year'].map(r => (
          <button key={r} onClick={() => setDateRange(r)}
            className={`px-4 h-10 text-[12px] font-medium capitalize transition-all ${dateRange === r ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>{r}</button>
        ))}
      </div>

      {renderReport()}
    </div>
  );
}
